import type { useLogging, useToast } from "Shared/Hooks";
import { OpenAPILink, type OpenAPILinkOptions } from "@orpc/openapi-client/fetch";
import { DynamicLink, createORPCClient, onError, onStart, onFinish, type ClientContext } from "@orpc/client";
import type { JsonifiedClient } from "@orpc/openapi-client";
import { ORPCError, ValidationError, type AnyContractRouter, type ContractRouterClient } from "@orpc/contract";
import { createContext, use } from "react";
import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import { RequestValidationPlugin, ResponseValidationPlugin } from "@orpc/contract/plugins";
import type { contract } from "./contract";
import { contractLoaders } from "./contractLoaders";
import { markThrottled, newGuid } from "Shared/Utils";
import { fetchFunction } from "./useFetch";
import type { TRootStore } from "Store/RootStore";
import { getBaseUrl } from "./baseUrl";
import { getChunkLoadFailure } from "Components/Pages/ChunkLoadRecovery";
import { stringifyForTelemetry } from "Core/Telemetry/redactSensitiveData";

type ApiName = keyof typeof contract;

const isApiName = (name: string | undefined): name is ApiName => name !== undefined && Object.prototype.hasOwnProperty.call(contractLoaders, name);

/** APIs the shell calls on every page (sign-in, header, user info). Their contracts start loading with the client, while sign-in runs. */
const preloadedApis: readonly ApiName[] = ["coreApi", "shasApi", "oncallApi", "ospMetricsApi"];

/** Rspack forgets a chunk that failed to load, so a second import requests the file again. */
const loadContract = async (api: ApiName) => {
    try {
        return await contractLoaders[api]();
    } catch {
        return contractLoaders[api]();
    }
};

const getContractLoadFailureMessage = (failureType: string | undefined) => {
    if (navigator.onLine === false) {
        return "You seem to be offline. Reconnect, then reload the page.";
    }
    // "missing": the server answered with another page instead of the file, as when a new version has replaced it.
    if (failureType === "missing") {
        return "A new version of M365 Pulse was released while this page was open. Reload the page to get it.";
    }
    return "Reload the page to try again.";
};

export interface ORPCClientDependencies {
    auth: Pick<TRootStore["AuthStore"], "getToken">;
    appSettings: Pick<TRootStore["AppSettingStore"], "settings">;
    logging: Pick<ReturnType<typeof useLogging>, "startTrackEvent" | "stopTrackEvent" | "trackTrace" | "trackException">;
    showToastNotification: ReturnType<typeof useToast>["showToastNotification"];
}

const createAppORPCClient = ({ auth, appSettings, logging, showToastNotification }: ORPCClientDependencies) => {
    const { startTrackEvent, stopTrackEvent, trackTrace, trackException } = logging;
    // Shared by the per-API links below; each link gets its own copy of `interceptors`, because the validation plugins add to it.
    const linkOptions: OpenAPILinkOptions<ClientContext> = {
        // Settings are read per call, so the one shared client always uses the current values.
        url: (context, path) => getBaseUrl(path, appSettings.settings),
        fetch: fetchFunction,
        headers: async (options, path) => {
            const correlationId = String(options.context["X-Correlation"] ?? "");
            return {
                Authorization: "Bearer " + (await auth.getToken()),
                "X-Correlation": correlationId
            };
        },
        interceptors: [
            onStart((options) => {
                options.context["X-Correlation"] = newGuid();
                startTrackEvent("oRPC API Call: " + options.path.join("/"));
            }),
            onFinish((state, options) => {
                stopTrackEvent("oRPC API Call: " + options.path.join("/"), options.context);
            }),
            onError((error, options) => {
                // The request's correlation ID, which error alerts show (M365Alert reads it from LogStore via trackException).
                const correlationId = String(options.context["X-Correlation"] ?? "");
                if (error instanceof ValidationError) {
                    trackException("aaa115d3-245f-4afa-8caa-cec7f89eddc5", error, "oRPC call failed with response validation error", {
                        CorrelationID: correlationId,
                        Path: options.path.join("/"),
                        Input: stringifyForTelemetry(options.input),
                        Issues: error.issues.map((issue) => issue.message).join(", "),
                        ...options.context
                    });
                    return;
                }
                if (error instanceof ORPCError) {
                    const errorCode = String(error.code);
                    if (["NOT_FOUND", "UNAUTHORIZED", "FORBIDDEN"].includes(errorCode)) {
                        trackTrace("7b87aeda-9afb-48e0-aef9-b3732ded83fc", "API is returned with errors 40*", {
                            CorrelationID: correlationId,
                            Error: error.message,
                            Path: options.path.join("/"),
                            Input: stringifyForTelemetry(options.input),
                            ...options.context
                        });
                        return;
                    }
                    if (errorCode === "TOO_MANY_REQUESTS") {
                        markThrottled("kusto");
                        trackTrace("7b87aeda-9afb-48e0-aef9-b3732ded83fc", "API is returned with 429 throttling", {
                            CorrelationID: correlationId,
                            Error: error.message,
                            Path: options.path.join("/"),
                            Input: stringifyForTelemetry(options.input),
                            ...options.context
                        });
                        if (options.context.suppressGlobalErrorToast === true) {
                            return;
                        }
                        showToastNotification({
                            title: "Kusto throttling: service is experiencing high demand. Please try again in a few moments.",
                            toastType: "warning",
                            duration: "Long",
                            aggregateType: "ThrottleWarning"
                        });
                        return;
                    }
                    if (error.code === "AbortError") {
                        trackException("aaa115d3-245f-4afa-8caa-cec7f89eddc5", error, "oRPC call failed with time out", {
                            CorrelationID: correlationId,
                            Path: options.path.join("/"),
                            Input: stringifyForTelemetry(options.input),
                            ...options.context
                        });
                        return;
                    }
                    if (error.code === "BAD_REQUEST" && "cause" in error && error.cause instanceof ValidationError) {
                        trackException("25e59377-48e4-4ce7-a132-ebeee6806e3e", error, "oRPC call failed with request validation error", {
                            CorrelationID: correlationId,
                            Path: options.path.join("/"),
                            Input: stringifyForTelemetry(options.input),
                            Issues: error.cause.issues.map((issue) => issue.message).join(", "),
                            ...options.context
                        });
                        return;
                    }
                    const rawErrorData: unknown = error.data;
                    const errorBody =
                        typeof rawErrorData === "object" && rawErrorData !== null && "body" in rawErrorData
                            ? typeof rawErrorData.body === "string"
                                ? rawErrorData.body
                                : JSON.stringify(rawErrorData.body)
                            : "";
                    trackException("aaa115d3-245f-4afa-8caa-cec7f89eddc5", error, `oRPC call failed with ${error.status} ${errorCode}`, {
                        CorrelationID: correlationId,
                        Path: options.path.join("/"),
                        Input: stringifyForTelemetry(options.input),
                        Issues: errorBody,
                        ...options.context
                    });
                    if (options.context.suppressGlobalErrorToast === true) {
                        return;
                    }
                    showToastNotification({
                        title: "There are errors on some service calls",
                        toastType: "error",
                        duration: "Long",
                        aggregateType: "ApiError"
                    });
                }
            })
        ]
    };

    const createApiLink = (apiContract: AnyContractRouter) =>
        new OpenAPILink(apiContract, {
            ...linkOptions,
            interceptors: [...(linkOptions.interceptors ?? [])],
            plugins: [new RequestValidationPlugin(apiContract), new ResponseValidationPlugin(apiContract)]
        });

    const apiLinks = new Map<ApiName, Promise<OpenAPILink<ClientContext>>>();
    const loadApiLink = async (api: ApiName) => {
        try {
            return createApiLink({ [api]: await loadContract(api) });
        } catch (error) {
            // A failed load isn't kept, so the next call to this API tries again.
            apiLinks.delete(api);
            throw error;
        }
    };
    const getApiLink = (api: ApiName) => {
        let apiLink = apiLinks.get(api);
        if (!apiLink) {
            apiLink = loadApiLink(api);
            apiLinks.set(api, apiLink);
        }
        return apiLink;
    };
    const preloadApiLink = async (api: ApiName) => {
        try {
            await getApiLink(api);
        } catch {
            // Nothing to report yet: the first call to this API loads it again and reports a failure then.
        }
    };

    // Each API's contract (routes and zod schemas) loads on its first call instead of in the startup bundle.
    const link = new DynamicLink<ClientContext>(async (_options, path) => {
        const api = path[0];
        if (!isApiName(api)) {
            throw new Error(`Unknown oRPC API "${path.join("/")}"`);
        }
        try {
            return await getApiLink(api);
        } catch (error) {
            const chunkLoadFailure = getChunkLoadFailure(error);
            trackException("6c3f7a52-1e0b-4d8e-9a57-2f4b8d1c0e93", error, "oRPC API contract failed to load", {
                Path: path.join("/"),
                ChunkRequest: chunkLoadFailure?.request ?? "",
                FailureType: chunkLoadFailure?.failureType ?? ""
            });
            showToastNotification({
                title: "Part of M365 Pulse couldn't load",
                message: getContractLoadFailureMessage(chunkLoadFailure?.failureType),
                toastType: "error",
                duration: "Long",
                aggregateType: "ContractLoadError"
            });
            throw error;
        }
    });
    preloadedApis.forEach((api) => void preloadApiLink(api));

    const client: JsonifiedClient<ContractRouterClient<typeof contract>> = createORPCClient(link);
    return client;
};

/** Builds the app's one oRPC client and its TanStack Query utils. Called once, by ORPCClientProvider. */
export const createSharedORPCClient = (dependencies: ORPCClientDependencies) => {
    const client = createAppORPCClient(dependencies);
    return { client, query: createTanstackQueryUtils(client) };
};

export type SharedORPCClient = ReturnType<typeof createSharedORPCClient>;

export const ORPCClientContext = createContext<SharedORPCClient | null>(null);

const useSharedORPCClient = () => {
    const shared = use(ORPCClientContext);
    if (shared === null) {
        throw new Error("useORPCClient and useORPCQuery must be used within ORPCClientProvider");
    }
    return shared;
};

/** The app's shared oRPC client (one instance, created by ORPCClientProvider). */
export const useORPCClient = () => useSharedORPCClient().client;

export const getORPCQueryOptions = (signal?: AbortSignal, suppressGlobalErrorToast = false) => ({
    signal,
    context: suppressGlobalErrorToast ? { suppressGlobalErrorToast: true } : {}
});

/** TanStack Query utils (`.queryOptions()`, `.mutationOptions()`, `.key()`) for the shared oRPC client. */
export const useORPCQuery = () => useSharedORPCClient().query;

export type ORPCClient = ReturnType<typeof useORPCClient>;

export type PendingApprovalProxy = Awaited<ReturnType<ORPCClient["accessApi"]["getPendingApprovalsEligibility"]>>[number];
